import React, { useMemo } from "react";
import { Skeleton } from "antd";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RootState } from "../../../redux/store";
import {
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryDescription,
  ProfitSection,
  ProfitAmount,
  MarginBadge,
  MarginText,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
} from "./styles";
import { DeliverySummaryProps } from "./types";
import {
  addCommasInPrices,
  generateKeyMetrics,
  SUMMARY_TEXTS,
  ARROW_ICON_CONFIG,
  SKELETON_CONFIG,
} from "./constants";

const DeliverySummary: React.FC<DeliverySummaryProps> = ({ merchant, storeId }) => {
  const { deliveryData, loading } = useSelector(
    (state: RootState) => state.deliveryCostAnalysis
  );

  const { keyMetrics, totalDeliveryProfit, deliveryProfitMargin } = useMemo(() => {
    const summary = deliveryData?.summary;
    if (!summary) {
      return {
        keyMetrics: [],
        totalDeliveryProfit: 0,
        deliveryProfitMargin: 0,
      };
    }
    const totalDeliveryProfit = parseFloat(String(summary.totalDeliveryProfit)) || 0;
    const deliveryProfitMargin = parseFloat(String(summary.deliveryProfitMargin)) || 0;
    const keyMetrics = generateKeyMetrics(summary);
    return {
      keyMetrics,
      totalDeliveryProfit,
      deliveryProfitMargin,
    };
  }, [deliveryData]);

  return (
    <SummaryCard>
      <SummaryHeader>
        <SummaryTitle>{SUMMARY_TEXTS.title}</SummaryTitle>
      </SummaryHeader>

      <SummaryDescription>
        {SUMMARY_TEXTS.description}
      </SummaryDescription>

      <ProfitSection>
        {loading ? (
          <Skeleton active paragraph={{ rows: 0 }} />
        ) : (
          <>
            <ProfitAmount>
              ${addCommasInPrices(parseFloat(String(totalDeliveryProfit)).toFixed(2))}
            </ProfitAmount>
            <MarginBadge>
              <FontAwesomeIcon
                icon={ARROW_ICON_CONFIG.icon}
                style={{ color: ARROW_ICON_CONFIG.color, fontSize: ARROW_ICON_CONFIG.fontSize }}
              />
              <MarginText>
                {parseFloat(String(deliveryProfitMargin)).toFixed(2)}
                {SUMMARY_TEXTS.marginLabel}
              </MarginText>
            </MarginBadge>
          </>
        )}
      </ProfitSection>

      {loading ? (
        <MetricsGrid>
          {Array.from({ length: SKELETON_CONFIG.metricCardsCount }).map((_, index) => (
            <MetricCard key={index}>
              <Skeleton active paragraph={{ rows: SKELETON_CONFIG.skeletonRows }} />
            </MetricCard>
          ))}
        </MetricsGrid>
      ) : (
        <MetricsGrid>
          {keyMetrics.map((metric) => (
            <MetricCard key={metric.id}>
              <MetricHeader>
                <MetricIcon color={metric.color}>
                  <FontAwesomeIcon icon={metric.icon} />
                </MetricIcon>
                <MetricTitle>{metric.title}</MetricTitle>
              </MetricHeader>
              <MetricValue>{metric.value}</MetricValue>
            </MetricCard>
          ))}
        </MetricsGrid>
      )}
    </SummaryCard>
  );
};

export default DeliverySummary;
